export type StreamLainEdgelessLayoutKind =
  | 'database'
  | 'automation'
  | 'database-ghost'
  | 'automation-ghost';

export interface StreamLainEdgelessLayoutNode {
  readonly id: string;
  readonly elementId: string;
  readonly kind: StreamLainEdgelessLayoutKind;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface StreamLainEdgelessLayoutEdge {
  readonly sourceId: string;
  readonly targetId: string;
  readonly type: 'automation' | 'relation';
}

export interface StreamLainEdgelessLayoutOptions {
  readonly horizontalGap?: number;
  readonly verticalGap?: number;
  readonly componentGap?: number;
}

export interface StreamLainEdgelessLayoutPosition {
  readonly elementId: string;
  readonly x: number;
  readonly y: number;
}

const DEFAULT_HORIZONTAL_GAP = 160;
const DEFAULT_VERTICAL_GAP = 64;
const DEFAULT_COMPONENT_GAP = 128;
const AUTOMATION_EDGE_WEIGHT = 4;
const RELATION_EDGE_WEIGHT = 1;

export function computeStreamLainEdgelessLayout(
  nodes: readonly StreamLainEdgelessLayoutNode[],
  edges: readonly StreamLainEdgelessLayoutEdge[],
  options: StreamLainEdgelessLayoutOptions = {}
): StreamLainEdgelessLayoutPosition[] {
  if (nodes.length < 2) {
    return nodes.map(node => ({
      elementId: node.elementId,
      x: node.x,
      y: node.y,
    }));
  }

  const nodeByElementId = new Map(nodes.map(node => [node.elementId, node]));
  const scopedEdges = edges.filter(
    edge =>
      nodeByElementId.has(edge.sourceId) && nodeByElementId.has(edge.targetId)
  );
  const components = getConnectedComponents(nodes, scopedEdges);
  const orderedComponents = components.sort((a, b) => {
    const aBounds = getBounds(a);
    const bBounds = getBounds(b);
    return aBounds.y - bBounds.y || aBounds.x - bBounds.x;
  });

  const horizontalGap = options.horizontalGap ?? DEFAULT_HORIZONTAL_GAP;
  const verticalGap = options.verticalGap ?? DEFAULT_VERTICAL_GAP;
  const componentGap = options.componentGap ?? DEFAULT_COMPONENT_GAP;
  const overallBounds = getBounds(nodes);
  let cursorY = overallBounds.y;

  const positions: StreamLainEdgelessLayoutPosition[] = [];

  for (const component of orderedComponents) {
    const componentEdgeSet = new Set(component.map(node => node.elementId));
    const componentEdges = scopedEdges.filter(
      edge =>
        componentEdgeSet.has(edge.sourceId) &&
        componentEdgeSet.has(edge.targetId)
    );
    const layout = layoutComponent(component, componentEdges, {
      horizontalGap,
      verticalGap,
      originX: overallBounds.x,
      originY: cursorY,
    });

    positions.push(...layout.positions);
    cursorY += layout.height + componentGap;
  }

  return positions;
}

function getConnectedComponents(
  nodes: readonly StreamLainEdgelessLayoutNode[],
  edges: readonly StreamLainEdgelessLayoutEdge[]
): StreamLainEdgelessLayoutNode[][] {
  const nodeById = new Map(nodes.map(node => [node.elementId, node]));
  const adjacency = new Map<string, Set<string>>();

  for (const node of nodes) {
    adjacency.set(node.elementId, new Set());
  }

  for (const edge of edges) {
    if (!nodeById.has(edge.sourceId) || !nodeById.has(edge.targetId)) continue;
    adjacency.get(edge.sourceId)?.add(edge.targetId);
    adjacency.get(edge.targetId)?.add(edge.sourceId);
  }

  const visited = new Set<string>();
  const components: StreamLainEdgelessLayoutNode[][] = [];

  for (const node of nodes) {
    if (visited.has(node.elementId)) continue;
    const stack = [node.elementId];
    const component: StreamLainEdgelessLayoutNode[] = [];
    visited.add(node.elementId);

    while (stack.length > 0) {
      const current = stack.pop()!;
      const currentNode = nodeById.get(current);
      if (currentNode) component.push(currentNode);

      for (const next of adjacency.get(current) ?? []) {
        if (visited.has(next)) continue;
        visited.add(next);
        stack.push(next);
      }
    }

    components.push(component);
  }

  return components;
}

function layoutComponent(
  nodes: readonly StreamLainEdgelessLayoutNode[],
  edges: readonly StreamLainEdgelessLayoutEdge[],
  options: {
    readonly horizontalGap: number;
    readonly verticalGap: number;
    readonly originX: number;
    readonly originY: number;
  }
): {
  readonly positions: StreamLainEdgelessLayoutPosition[];
  readonly width: number;
  readonly height: number;
} {
  const ranks = assignRanks(nodes, edges);
  const layers = orderLayers(groupByRank(nodes, ranks), edges, ranks);
  const layerWidths = layers.map(layer =>
    Math.max(...layer.map(node => node.width))
  );
  const centerYById = assignRelaxedCenterY(layers, edges, options);
  const verticalBounds = getVerticalBounds(layers, centerYById);
  const positions: StreamLainEdgelessLayoutPosition[] = [];

  let x = options.originX;
  for (const [layerIndex, layer] of layers.entries()) {
    const layerWidth = layerWidths[layerIndex];

    for (const node of layer) {
      positions.push({
        elementId: node.elementId,
        x: x + Math.max(0, (layerWidth - node.width) / 2),
        y: (centerYById.get(node.elementId) ?? node.y) - node.height / 2,
      });
    }

    x += layerWidth + options.horizontalGap;
  }

  return {
    positions,
    width:
      layerWidths.reduce((sum, width) => sum + width, 0) +
      Math.max(0, layers.length - 1) * options.horizontalGap,
    height: verticalBounds.height,
  };
}

function assignRanks(
  nodes: readonly StreamLainEdgelessLayoutNode[],
  edges: readonly StreamLainEdgelessLayoutEdge[]
): Map<string, number> {
  const automationEdges = edges.filter(edge => edge.type === 'automation');
  if (automationEdges.length === 0) {
    return assignGridRanks(nodes);
  }

  const nodeIds = nodes.map(node => node.elementId);
  const sccs = getStronglyConnectedComponents(nodeIds, automationEdges);
  const sccByNode = new Map<string, number>();
  for (const [index, scc] of sccs.entries()) {
    for (const nodeId of scc) {
      sccByNode.set(nodeId, index);
    }
  }

  const dag = new Map<number, Set<number>>();
  const indegree = new Map<number, number>();
  for (let i = 0; i < sccs.length; i++) {
    dag.set(i, new Set());
    indegree.set(i, 0);
  }

  for (const edge of automationEdges) {
    const source = sccByNode.get(edge.sourceId);
    const target = sccByNode.get(edge.targetId);
    if (source === undefined || target === undefined || source === target) {
      continue;
    }
    const targets = dag.get(source)!;
    if (!targets.has(target)) {
      targets.add(target);
      indegree.set(target, (indegree.get(target) ?? 0) + 1);
    }
  }

  const queue = [...indegree.entries()]
    .filter(([, count]) => count === 0)
    .map(([id]) => id);
  const sccRank = new Map<number, number>();
  for (const id of queue) {
    sccRank.set(id, 0);
  }

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentRank = sccRank.get(current) ?? 0;
    for (const next of dag.get(current) ?? []) {
      sccRank.set(next, Math.max(sccRank.get(next) ?? 0, currentRank + 1));
      const nextIndegree = (indegree.get(next) ?? 0) - 1;
      indegree.set(next, nextIndegree);
      if (nextIndegree === 0) queue.push(next);
    }
  }

  const ranks = new Map<string, number>();
  for (const node of nodes) {
    const scc = sccByNode.get(node.elementId);
    ranks.set(node.elementId, scc === undefined ? 0 : (sccRank.get(scc) ?? 0));
  }
  return ranks;
}

function assignGridRanks(
  nodes: readonly StreamLainEdgelessLayoutNode[]
): Map<string, number> {
  const sorted = [...nodes].sort((a, b) => a.x - b.x || a.y - b.y);
  const columnCount = Math.max(1, Math.ceil(Math.sqrt(nodes.length)));
  const ranks = new Map<string, number>();
  for (const [index, node] of sorted.entries()) {
    ranks.set(node.elementId, index % columnCount);
  }
  return ranks;
}

function getStronglyConnectedComponents(
  nodeIds: readonly string[],
  edges: readonly StreamLainEdgelessLayoutEdge[]
): string[][] {
  const adjacency = new Map<string, string[]>();
  for (const nodeId of nodeIds) {
    adjacency.set(nodeId, []);
  }
  for (const edge of edges) {
    adjacency.get(edge.sourceId)?.push(edge.targetId);
  }

  const stack: string[] = [];
  const onStack = new Set<string>();
  const indices = new Map<string, number>();
  const lowLinks = new Map<string, number>();
  const components: string[][] = [];
  let index = 0;

  const visit = (nodeId: string) => {
    indices.set(nodeId, index);
    lowLinks.set(nodeId, index);
    index++;
    stack.push(nodeId);
    onStack.add(nodeId);

    for (const next of adjacency.get(nodeId) ?? []) {
      if (!indices.has(next)) {
        visit(next);
        lowLinks.set(
          nodeId,
          Math.min(lowLinks.get(nodeId)!, lowLinks.get(next)!)
        );
      } else if (onStack.has(next)) {
        lowLinks.set(
          nodeId,
          Math.min(lowLinks.get(nodeId)!, indices.get(next)!)
        );
      }
    }

    if (lowLinks.get(nodeId) !== indices.get(nodeId)) return;

    const component: string[] = [];
    let current: string | undefined;
    do {
      current = stack.pop();
      if (!current) break;
      onStack.delete(current);
      component.push(current);
    } while (current !== nodeId);
    components.push(component);
  };

  for (const nodeId of nodeIds) {
    if (!indices.has(nodeId)) visit(nodeId);
  }

  return components;
}

function groupByRank(
  nodes: readonly StreamLainEdgelessLayoutNode[],
  ranks: Map<string, number>
): StreamLainEdgelessLayoutNode[][] {
  const layers = new Map<number, StreamLainEdgelessLayoutNode[]>();
  for (const node of nodes) {
    const rank = ranks.get(node.elementId) ?? 0;
    const layer = layers.get(rank) ?? [];
    layer.push(node);
    layers.set(rank, layer);
  }

  return [...layers.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, layer]) => layer);
}

function orderLayers(
  rawLayers: readonly StreamLainEdgelessLayoutNode[][],
  edges: readonly StreamLainEdgelessLayoutEdge[],
  ranks: ReadonlyMap<string, number>
): StreamLainEdgelessLayoutNode[][] {
  const mainPath = findMainPath(rawLayers.flat(), edges, ranks);
  const layers = rawLayers.map(layer =>
    [...layer].sort(
      (a, b) =>
        mainPathScore(a, mainPath) - mainPathScore(b, mainPath) ||
        a.y - b.y ||
        a.x - b.x
    )
  );

  for (let pass = 0; pass < 8; pass++) {
    for (let i = 1; i < layers.length; i++) {
      layers[i] = sortByNeighborBarycenter(layers[i], layers, edges, i, -1);
    }
    for (let i = layers.length - 2; i >= 0; i--) {
      layers[i] = sortByNeighborBarycenter(layers[i], layers, edges, i, 1);
    }
  }

  return layers;
}

function sortByNeighborBarycenter(
  layer: readonly StreamLainEdgelessLayoutNode[],
  layers: readonly StreamLainEdgelessLayoutNode[][],
  edges: readonly StreamLainEdgelessLayoutEdge[],
  layerIndex: number,
  direction: -1 | 1
): StreamLainEdgelessLayoutNode[] {
  const orderById = buildLayerOrder(layers);
  const layerIds = new Set(layer.map(node => node.elementId));

  return [...layer].sort((a, b) => {
    const aScore = getNeighborBarycenter(
      a.elementId,
      layerIds,
      edges,
      orderById,
      layerIndex,
      direction
    );
    const bScore = getNeighborBarycenter(
      b.elementId,
      layerIds,
      edges,
      orderById,
      layerIndex,
      direction
    );

    return (
      (aScore ?? orderById.get(a.elementId)?.order ?? 0) -
        (bScore ?? orderById.get(b.elementId)?.order ?? 0) ||
      a.y - b.y ||
      a.x - b.x
    );
  });
}

function buildLayerOrder(layers: readonly StreamLainEdgelessLayoutNode[][]) {
  const orderById = new Map<string, { layerIndex: number; order: number }>();
  for (const [layerIndex, layer] of layers.entries()) {
    for (const [order, node] of layer.entries()) {
      orderById.set(node.elementId, { layerIndex, order });
    }
  }
  return orderById;
}

function getNeighborBarycenter(
  nodeId: string,
  layerIds: ReadonlySet<string>,
  edges: readonly StreamLainEdgelessLayoutEdge[],
  orderById: ReadonlyMap<string, { layerIndex: number; order: number }>,
  layerIndex: number,
  direction: -1 | 1
): number | null {
  let weightedSum = 0;
  let totalWeight = 0;

  for (const edge of edges) {
    const otherId =
      edge.sourceId === nodeId
        ? edge.targetId
        : edge.targetId === nodeId
          ? edge.sourceId
          : null;
    if (!otherId || layerIds.has(otherId)) continue;

    const otherOrder = orderById.get(otherId);
    if (!otherOrder) continue;
    if (
      direction === -1
        ? otherOrder.layerIndex >= layerIndex
        : otherOrder.layerIndex <= layerIndex
    ) {
      continue;
    }

    const weight = getEdgeWeight(edge);
    weightedSum += otherOrder.order * weight;
    totalWeight += weight;
  }

  return totalWeight === 0 ? null : weightedSum / totalWeight;
}

function assignRelaxedCenterY(
  layers: readonly StreamLainEdgelessLayoutNode[][],
  edges: readonly StreamLainEdgelessLayoutEdge[],
  options: {
    readonly verticalGap: number;
    readonly originY: number;
  }
): Map<string, number> {
  let centerYById = assignInitialCenterY(layers, options);

  for (let pass = 0; pass < 6; pass++) {
    const targets = new Map<string, number>();

    for (const layer of layers) {
      for (const node of layer) {
        const neighborCenter = getWeightedNeighborCenter(
          node.elementId,
          edges,
          centerYById
        );
        targets.set(
          node.elementId,
          neighborCenter === null
            ? (centerYById.get(node.elementId) ?? node.y + node.height / 2)
            : neighborCenter
        );
      }
    }

    const nextCenterYById = new Map<string, number>();
    for (const layer of layers) {
      for (const [nodeId, center] of compactLayerToTargets(
        layer,
        targets,
        centerYById,
        options.verticalGap
      )) {
        nextCenterYById.set(nodeId, center);
      }
    }
    centerYById = normalizeCenterY(layers, nextCenterYById, options.originY);
  }

  return centerYById;
}

function assignInitialCenterY(
  layers: readonly StreamLainEdgelessLayoutNode[][],
  options: {
    readonly verticalGap: number;
    readonly originY: number;
  }
): Map<string, number> {
  const layerHeights = layers.map(getLayerHeight(options.verticalGap));
  const componentHeight = Math.max(...layerHeights);
  const centerYById = new Map<string, number>();

  for (const [layerIndex, layer] of layers.entries()) {
    let y =
      options.originY +
      Math.max(0, (componentHeight - layerHeights[layerIndex]) / 2);

    for (const node of layer) {
      centerYById.set(node.elementId, y + node.height / 2);
      y += node.height + options.verticalGap;
    }
  }

  return centerYById;
}

function compactLayerToTargets(
  layer: readonly StreamLainEdgelessLayoutNode[],
  targets: ReadonlyMap<string, number>,
  previousCenterYById: ReadonlyMap<string, number>,
  verticalGap: number
): Map<string, number> {
  const ordered = [...layer].sort((a, b) => {
    const aTarget =
      targets.get(a.elementId) ??
      previousCenterYById.get(a.elementId) ??
      a.y + a.height / 2;
    const bTarget =
      targets.get(b.elementId) ??
      previousCenterYById.get(b.elementId) ??
      b.y + b.height / 2;
    return aTarget - bTarget || a.y - b.y || a.x - b.x;
  });

  const tops = new Map<string, number>();
  for (const [index, node] of ordered.entries()) {
    const targetTop = (targets.get(node.elementId) ?? node.y) - node.height / 2;
    const previous = index === 0 ? null : ordered[index - 1];
    const minTop = previous
      ? (tops.get(previous.elementId) ?? previous.y) +
        previous.height +
        verticalGap
      : targetTop;
    tops.set(node.elementId, Math.max(targetTop, minTop));
  }

  for (let index = ordered.length - 2; index >= 0; index--) {
    const node = ordered[index];
    const next = ordered[index + 1];
    const targetTop = (targets.get(node.elementId) ?? node.y) - node.height / 2;
    const maxTop =
      (tops.get(next.elementId) ?? next.y) - verticalGap - node.height;
    tops.set(
      node.elementId,
      Math.min(tops.get(node.elementId) ?? targetTop, maxTop)
    );
  }

  const centers = new Map<string, number>();
  for (const node of ordered) {
    centers.set(
      node.elementId,
      (tops.get(node.elementId) ?? node.y) + node.height / 2
    );
  }
  return centers;
}

function normalizeCenterY(
  layers: readonly StreamLainEdgelessLayoutNode[][],
  centerYById: ReadonlyMap<string, number>,
  originY: number
): Map<string, number> {
  const bounds = getVerticalBounds(layers, centerYById);
  const delta = originY - bounds.minY;
  const normalized = new Map<string, number>();
  for (const [nodeId, center] of centerYById) {
    normalized.set(nodeId, center + delta);
  }
  return normalized;
}

function getVerticalBounds(
  layers: readonly StreamLainEdgelessLayoutNode[][],
  centerYById: ReadonlyMap<string, number>
): { minY: number; maxY: number; height: number } {
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const layer of layers) {
    for (const node of layer) {
      const center =
        centerYById.get(node.elementId) ?? node.y + node.height / 2;
      minY = Math.min(minY, center - node.height / 2);
      maxY = Math.max(maxY, center + node.height / 2);
    }
  }

  return {
    minY,
    maxY,
    height: maxY - minY,
  };
}

function getWeightedNeighborCenter(
  nodeId: string,
  edges: readonly StreamLainEdgelessLayoutEdge[],
  centerYById: ReadonlyMap<string, number>
): number | null {
  let weightedSum = 0;
  let totalWeight = 0;

  for (const edge of edges) {
    const otherId =
      edge.sourceId === nodeId
        ? edge.targetId
        : edge.targetId === nodeId
          ? edge.sourceId
          : null;
    if (!otherId) continue;

    const otherCenter = centerYById.get(otherId);
    if (otherCenter === undefined) continue;

    const weight = getEdgeWeight(edge);
    weightedSum += otherCenter * weight;
    totalWeight += weight;
  }

  return totalWeight === 0 ? null : weightedSum / totalWeight;
}

function getLayerHeight(verticalGap: number) {
  return (layer: readonly StreamLainEdgelessLayoutNode[]) =>
    layer.reduce((sum, node) => sum + node.height, 0) +
    Math.max(0, layer.length - 1) * verticalGap;
}

function findMainPath(
  nodes: readonly StreamLainEdgelessLayoutNode[],
  edges: readonly StreamLainEdgelessLayoutEdge[],
  ranks: ReadonlyMap<string, number>
): ReadonlySet<string> {
  const nodeIds = new Set(nodes.map(node => node.elementId));
  const outgoing = new Map<string, string[]>();
  for (const node of nodes) {
    outgoing.set(node.elementId, []);
  }

  for (const edge of edges) {
    if (
      edge.type !== 'automation' ||
      !nodeIds.has(edge.sourceId) ||
      !nodeIds.has(edge.targetId)
    ) {
      continue;
    }
    if ((ranks.get(edge.targetId) ?? 0) <= (ranks.get(edge.sourceId) ?? 0)) {
      continue;
    }
    outgoing.get(edge.sourceId)?.push(edge.targetId);
  }

  const sortedNodes = [...nodes].sort(
    (a, b) =>
      (ranks.get(b.elementId) ?? 0) - (ranks.get(a.elementId) ?? 0) ||
      a.y - b.y ||
      a.x - b.x
  );
  const scoreById = new Map<string, number>();
  const nextById = new Map<string, string>();

  for (const node of sortedNodes) {
    let bestScore = 1;
    let bestNext: string | null = null;
    for (const next of outgoing.get(node.elementId) ?? []) {
      const score = 1 + (scoreById.get(next) ?? 1);
      if (score > bestScore) {
        bestScore = score;
        bestNext = next;
      }
    }
    scoreById.set(node.elementId, bestScore);
    if (bestNext) nextById.set(node.elementId, bestNext);
  }

  const start = [...nodes].sort(
    (a, b) =>
      (scoreById.get(b.elementId) ?? 1) - (scoreById.get(a.elementId) ?? 1) ||
      (ranks.get(a.elementId) ?? 0) - (ranks.get(b.elementId) ?? 0) ||
      a.y - b.y ||
      a.x - b.x
  )[0];
  if (!start) return new Set();

  const mainPath = new Set<string>();
  let current: string | undefined = start.elementId;
  while (current && !mainPath.has(current)) {
    mainPath.add(current);
    current = nextById.get(current);
  }
  return mainPath;
}

function mainPathScore(
  node: StreamLainEdgelessLayoutNode,
  mainPath: ReadonlySet<string>
): number {
  return mainPath.has(node.elementId) ? 0 : 1;
}

function getEdgeWeight(edge: StreamLainEdgelessLayoutEdge): number {
  return edge.type === 'automation'
    ? AUTOMATION_EDGE_WEIGHT
    : RELATION_EDGE_WEIGHT;
}

function getBounds(nodes: readonly StreamLainEdgelessLayoutNode[]) {
  const minX = Math.min(...nodes.map(node => node.x));
  const minY = Math.min(...nodes.map(node => node.y));
  const maxX = Math.max(...nodes.map(node => node.x + node.width));
  const maxY = Math.max(...nodes.map(node => node.y + node.height));
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}
